import { broadcastLogout, useAuthStore } from '@/stores/AuthStore.ts';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';

import {
  OrganizationRights,
  OrganizationRoles,
  type OrganizationUser,
  PlatformRights,
  Rights,
  Roles,
} from '@/clients/v1';
import { captureError } from '@/composables/Sentry.ts';
import { clearAuthStorage } from '@/composables/SessionStorage.ts';
import { clearGoogleDriveTokenCache } from '@/composables/GoogleDriveToken.ts';

const PRESERVED_KEYS = ['selectedLanguage', 'list:page-size'];
const PRESERVED_PREFIXES = ['visibleColumns_'];

type LoginRedirectOptions = {
  reauthenticate?: boolean;
  returnTo?: string;
};

const platformRightsOf = (user: OrganizationUser): string[] =>
  Object.values(user.platform_rights ?? []);

const organizationRightsOf = (user: OrganizationUser): string[] =>
  Object.values(user.organization_rights ?? []);

const projectRightsOf = (user: OrganizationUser, projectId: string): string[] =>
  Object.values(user.project_rights?.[projectId] ?? []);

const isPreservedKey = (key: string): boolean =>
  PRESERVED_KEYS.includes(key) || PRESERVED_PREFIXES.some((prefix) => key.startsWith(prefix));

export const isSuperAdmin = (): boolean => {
  const { user } = useAuthStore();

  if (!user) return false;

  return user.is_super_admin ?? false;
};

export const isOrgAdmin = (): boolean => {
  const { user } = useAuthStore();
  const { configData } = useOrganizationsStore();

  if (isSuperAdmin()) return true;
  if (!user || !configData) return false;

  const current = user.organization_roles?.find(
    (role) => role.organization_id === configData.organization_id,
  );

  return current?.role === OrganizationRoles.Admin;
};

export const hasPlatformRights = (rightsRule: PlatformRights): boolean => {
  const { user } = useAuthStore();

  if (!user) return false;

  return platformRightsOf(user).includes(rightsRule);
};

// Rights the backend resolves from the active organization's roles alone: platform-wide organization access does
// not grant them (`has_user_registration_review_rights_dep`).
const ACTIVE_ORGANIZATION_ONLY_RIGHTS: OrganizationRights[] = [
  OrganizationRights.OrganizationsProjectsRegistrationReview,
];

export const hasOrganizationRights = (rightsRule: OrganizationRights): boolean => {
  const { user } = useAuthStore();

  if (!user) return false;

  const platformGrants =
    !ACTIVE_ORGANIZATION_ONLY_RIGHTS.includes(rightsRule) &&
    platformRightsOf(user).includes(PlatformRights.PlatformOrganizationsFullAccess);

  return platformGrants || organizationRightsOf(user).includes(rightsRule);
};

export const hasProjectRights = (projectId: string, rightsRule: Rights): boolean => {
  const { user } = useAuthStore();
  const { configData } = useOrganizationsStore();

  if (!user || !configData) return false;

  return (
    platformRightsOf(user).includes(PlatformRights.PlatformProjectsFullAccess) ||
    organizationRightsOf(user).includes(OrganizationRights.OrganizationsProjectsFullAccess) ||
    projectRightsOf(user, projectId).includes(rightsRule)
  );
};

/**
 * Whether the user holds a right through a role on the AI System itself.
 *
 * Unlike `hasProjectRights`, organization- or platform-wide project access does not count. That is
 * what separates an owner from a risk or policy manager, who reaches every AI System in the
 * organization without being on any one of them.
 */
export const hasDirectProjectRights = (projectId: string, rightsRule: Rights): boolean => {
  const { user } = useAuthStore();

  if (!user) return false;

  return projectRightsOf(user, projectId).includes(rightsRule);
};

export const userProjectRoles = (projectId: string): Roles[] => {
  const { user } = useAuthStore();

  return (
    user?.project_roles?.filter((role) => role.project_id === projectId).map((role) => role.role) ??
    []
  );
};

export const cleanStorage = () => {
  const authStore = useAuthStore();
  const organizationsStore = useOrganizationsStore();

  clearAuthStorage();
  Object.keys(localStorage).forEach((key) => {
    if (!isPreservedKey(key)) localStorage.removeItem(key);
  });

  sessionStorage.clear();

  authStore.resetStore();
  organizationsStore.resetStore();
  clearGoogleDriveTokenCache();

  broadcastLogout();
};

export function useAuth() {
  const redirectToLogin = (options: LoginRedirectOptions = {}) => {
    const loginUrl = new URL(`${import.meta.env.VITE_API_URL}/api/auth/login`);

    if (options.reauthenticate) loginUrl.searchParams.set('reauthenticate', 'true');
    if (options.returnTo) loginUrl.searchParams.set('return_to', options.returnTo);

    window.location.href = loginUrl.toString();
  };

  const logout = async () => {
    const authStore = useAuthStore();
    const redirectUrl = await authStore.logoutFromIdp();

    cleanStorage();
    window.location.assign(redirectUrl ?? `${window.location.origin}/login`);
  };

  const refreshToken = async () => {
    try {
      const authStore = useAuthStore();
      await authStore.getAccessTokenFromRefreshToken();
    } catch (error) {
      captureError(error, {
        message: 'Composable: Auth.ts, Function: refreshToken',
      });
    }
  };

  return { refreshToken, logout, redirectToLogin };
}
